import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Store as StoreIcon, Truck, Users } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'vendor') {
        navigate('/vendor');
      } else {
        toast.error('This dashboard is for admins and vendors only');
        localStorage.removeItem('r2yn_token');
        localStorage.removeItem('r2yn_user');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (email, password) => {
    setEmail(email);
    setPassword(password);
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'vendor') navigate('/vendor');
    } catch (err) {
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center bg-gradient-to-br from-white via-slate-50 to-slate-100">
        <div className="relative z-10 max-w-lg px-12">
          <div className="mb-8">
            <img src="/r2yn-logo-color.png" alt="R2YN - Rushing 2 Your Needs" className="h-20 w-auto mb-4" />
            <p className="text-sm text-muted-foreground font-medium">Rushing 2 Your Needs</p>
          </div>
          <p className="text-xl font-medium text-foreground/80 mb-8" data-font="display">
            Enterprise Delivery Marketplace Platform
          </p>
          <div className="space-y-4">
            {[
              { icon: ShieldCheck, label: 'Manage users, drivers & vendors' },
              { icon: StoreIcon, label: 'Full store & menu management' },
              { icon: Truck, label: 'Real-time order tracking' },
              { icon: Users, label: 'Analytics & revenue insights' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3 text-muted-foreground">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile brand */}
          <div className="lg:hidden text-center">
            <div className="inline-flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold" data-font="display">R2</span>
              </div>
              <h1 className="text-3xl font-bold tracking-tight" data-font="display">R2YN</h1>
            </div>
            <p className="text-sm text-muted-foreground">Enterprise Delivery Marketplace</p>
          </div>

          <Card className="border shadow-sm">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl" data-font="display">Sign in</CardTitle>
              <CardDescription>Enter your credentials to access the dashboard</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@r2yn.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-form-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-form-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="login-form-submit-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Sign In
                </Button>
              </form>

              <div className="mt-6">
                <p className="text-xs text-muted-foreground text-center mb-3">Quick access (demo)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => quickLogin('admin@r2yn.com', 'R2ynAdmin2026!')}
                    disabled={loading}
                    data-testid="quick-login-admin"
                  >
                    <ShieldCheck className="h-3 w-3 mr-1" /> Admin
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => quickLogin('demovendor@r2yn.com', 'Demo123456')}
                    disabled={loading}
                    data-testid="quick-login-vendor"
                  >
                    <StoreIcon className="h-3 w-3 mr-1" /> Vendor
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-center text-muted-foreground">
            R2YN Platform v2.0 — Rushing 2 Your Needs — Secure enterprise access
          </p>
        </div>
      </div>
    </div>
  );
}
